import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminStatus, rerunClient } from '../lib/api';
import type { ClientStatusFeatureKey, ClientStatusResponse, ClientStatusRow, FeatureStatus, PipelineStatus } from '../types';
import { useAuth } from '../lib/auth';
import { GBIBadge, GBIBtn, Icon, T, sharedStyles } from '../components/Shared';

// Operator's single screen for "who is broken" / "who was never set up".
// GET /api/admin/status is cheap (existing tables only, no new writes), so we
// poll it — see the visibility-driven effect below for why it pauses.
const POLL_MS = 30_000;

const FEATURE_COLUMNS: { key: ClientStatusFeatureKey; label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'heatmap', label: 'Heatmap' },
  { key: 'aiSearch', label: 'AI Search' },
  { key: 'aiInsight', label: 'AI Insight' },
  { key: 'reviews', label: 'Reviews' },
  { key: 'website', label: 'Website' },
];

const STATE_COLOR: Record<FeatureStatus['state'], string> = {
  ok: T.green, due: T.amber, overdue: T.magenta, missing: T.fgMuted,
};
const STAGE_COLOR: Record<ClientStatusRow['stage'], string> = {
  draft: T.fgMuted, ready: T.cyan, live: T.green, archived: T.borderBright,
};
const CAMPAIGN_COLOR: Record<ClientStatusRow['campaign'], string> = {
  missing: T.fgMuted, created: T.amber, started: T.green,
};

function fmtDate(ts: number | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function fmtDateTime(ts: number | null): string {
  if (!ts) return 'Never run';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? 'Never run' : d.toLocaleString('en-GB');
}

function agoLabel(ts: number | null, now: number): string {
  if (!ts) return '—';
  const secs = Math.max(0, Math.round((now - ts) / 1000));
  if (secs < 5) return 'just now';
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.round(mins / 60)}h ago`;
}

// ── Sorting ───────────────────────────────────────────────────
type SortKey = 'default' | 'name' | ClientStatusFeatureKey;
interface SortState { key: SortKey; dir: 'asc' | 'desc' }
const DEFAULT_SORT: SortState = { key: 'default', dir: 'asc' };

function compareRows(a: ClientStatusRow, b: ClientStatusRow, sort: SortState): number {
  if (sort.key === 'default') {
    // Attention first, then alphabetical — the "spot the broken one" order.
    const aAttn = a.attention.length > 0 ? 0 : 1;
    const bAttn = b.attention.length > 0 ? 0 : 1;
    if (aAttn !== bAttn) return aAttn - bAttn;
    return a.name.localeCompare(b.name);
  }
  const dir = sort.dir === 'asc' ? 1 : -1;
  if (sort.key === 'name') return a.name.localeCompare(b.name) * dir;
  const av = a.features[sort.key].lastUpdatedAt;
  const bv = b.features[sort.key].lastUpdatedAt;
  if (av === bv) return 0;
  if (av === null) return 1; // never-run always sorts last, regardless of direction
  if (bv === null) return -1;
  return (av - bv) * dir;
}

type ActionTarget = 'aiInsight' | 'data';
interface RowAction { loading: ActionTarget | null; message: { kind: 'success' | 'error'; text: string } | null }

export function AdminStatus() {
  const { user } = useAuth();
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<ClientStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [query, setQuery] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [notStartedOnly, setNotStartedOnly] = useState(false);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [actions, setActions] = useState<Record<string, RowAction>>({});
  const hasDataRef = useRef(false);
  const clearTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => { hasDataRef.current = data !== null; }, [data]);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setState('loading');
    try {
      const resp = await getAdminStatus();
      setData(resp);
      setState('ready');
      setError(null);
    } catch (err) {
      // A background poll failing shouldn't blank out a table that's already
      // showing good data — only surface the error when there's nothing else to show.
      if (showSpinner || !hasDataRef.current) {
        setState('error');
        setError(err instanceof Error ? err.message : String(err));
      }
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  // Poll every 30s; pause while the tab is hidden so a backgrounded dashboard
  // doesn't keep spending requests, and refresh immediately on refocus.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => { if (!timer) timer = setInterval(() => load(false), POLL_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVisibility = () => {
      if (document.hidden) stop();
      else { load(false); start(); }
    };
    document.addEventListener('visibilitychange', onVisibility);
    start();
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [load]);

  // Ticks the "last refreshed Xs ago" label without needing a fresh fetch.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => () => { Object.values(clearTimers.current).forEach(clearTimeout); }, []);

  const rows = data?.rows ?? [];
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(r => {
      if (attentionOnly && r.attention.length === 0) return false;
      if (notStartedOnly && r.stage === 'live') return false;
      if (q && !(r.name.toLowerCase().includes(q) || r.location.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, query, attentionOnly, notStartedOnly]);
  const sortedRows = useMemo(() => [...filteredRows].sort((a, b) => compareRows(a, b, sort)), [filteredRows, sort]);

  const onSort = (key: SortKey) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' ? 'asc' : 'desc' });
  };

  const runRerun = async (row: ClientStatusRow, target: ActionTarget) => {
    if (!row.clientId) return;
    const clientId = row.clientId;
    const confirmMsg = target === 'aiInsight'
      ? `Rerun AI Insight for ${row.name}? This costs about $0.62 in Claude usage and does not re-scan Maps.`
      : `Run a full data refresh for ${row.name}? This ALSO spends Local Falcon credits and DataForSEO quota, on top of the AI Insight cost.`;
    if (!confirm(confirmMsg)) return;

    clearTimeout(clearTimers.current[clientId]);
    setActions(a => ({ ...a, [clientId]: { loading: target, message: null } }));
    try {
      await rerunClient(clientId, target);
      setActions(a => ({ ...a, [clientId]: { loading: null, message: { kind: 'success', text: target === 'aiInsight' ? 'Report requeued.' : 'Refresh started.' } } }));
      load(false);
    } catch (err) {
      setActions(a => ({ ...a, [clientId]: { loading: null, message: { kind: 'error', text: err instanceof Error ? err.message : String(err) } } }));
    }
    clearTimers.current[clientId] = setTimeout(() => {
      setActions(a => ({ ...a, [clientId]: { ...a[clientId], message: null } }));
    }, 6000);
  };

  // Belt-and-suspenders: App.tsx already gates the route to admin/manager.
  if (user && user.role !== 'admin' && user.role !== 'manager') return null;

  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: '#f8f9fa', padding: '40px 32px' }}>
      <div style={{ maxWidth: 1440, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{
              fontFamily: sharedStyles.fontDisplay, fontWeight: 700, fontSize: 11,
              background: T.brandGradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6,
            }}>AI Intelligence — Internal Console</div>
            <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 28, fontWeight: 700, color: T.fgPrimary }}>Client Status</div>
            <div style={{ fontSize: 12, color: T.fgMuted, marginTop: 4 }}>
              {data ? `Last refreshed ${agoLabel(data.generatedAt, now)}` : 'Loading…'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Link to="/admin" style={{ textDecoration: 'none' }}>
              <button style={{
                background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8,
                padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: T.fgSecondary,
              }}>Back to Clients</button>
            </Link>
            <GBIBtn variant="secondary" size="sm" onClick={() => load(!data)} disabled={state === 'loading'}>
              {state === 'loading' ? 'Refreshing…' : 'Refresh'}
            </GBIBtn>
          </div>
        </div>

        {data && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
            <SummaryChip label="Total" value={data.summary.total} color={T.fgSecondary} />
            <SummaryChip label="Live" value={data.summary.live} color={T.green} />
            <SummaryChip label="Not started" value={data.summary.notStarted} color={T.fgMuted} />
            <SummaryChip label="Need attention" value={data.summary.needAttention} color={T.amber} />
            <SummaryChip label="Failing" value={data.summary.failing} color={T.magenta} />
          </div>
        )}

        {data && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
              <div style={{ fontFamily: sharedStyles.fontDisplay, fontSize: 15, fontWeight: 700, color: T.fgPrimary }}>Pipelines</div>
              <span style={{ fontSize: 12, color: T.fgMuted }}>Cycle day {data.cycleDayToday} of {data.cycleLength}</span>
            </div>
            <PipelineStrip pipelines={data.pipelines} now={now} />
          </div>
        )}

        {data && data.rows.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or location…"
              style={{
                flex: '0 1 300px', minWidth: 200, boxSizing: 'border-box',
                padding: '8px 10px', fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 8,
                outline: 'none', background: '#fff', color: T.fgPrimary,
              }}
            />
            <ToggleChip active={attentionOnly} onClick={() => setAttentionOnly(v => !v)} label="Needs attention only" />
            <ToggleChip active={notStartedOnly} onClick={() => setNotStartedOnly(v => !v)} label="Not started only" />
            <span style={{ fontSize: 12, color: T.fgMuted, marginLeft: 'auto' }}>
              {sortedRows.length} of {rows.length} rows
            </span>
          </div>
        )}

        {state === 'loading' && !data && (
          <div style={{ padding: 40, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>Loading client status…</div>
        )}

        {state === 'error' && (
          <div style={{
            padding: '12px 14px', background: 'rgba(232,80,26,0.08)',
            border: `1px solid ${T.magenta}`, borderRadius: 8, fontSize: 12, color: '#C03B0F',
          }}>
            <strong>Failed to load client status.</strong>
            <div style={{ marginTop: 6, fontFamily: sharedStyles.fontMono }}>{error}</div>
            <button onClick={() => load(true)} style={{
              marginTop: 10, background: 'transparent', border: `1px solid ${T.border}`,
              borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: T.fgSecondary,
            }}>Retry</button>
          </div>
        )}

        {data && data.rows.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: T.fgMuted, fontSize: 13 }}>No clients yet.</div>
        )}

        {data && data.rows.length > 0 && sortedRows.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: T.fgMuted, fontSize: 13, fontStyle: 'italic' }}>No rows match your filters.</div>
        )}

        {data && sortedRows.length > 0 && (
          <StatusTable rows={sortedRows} sort={sort} onSort={onSort} actions={actions} onRerun={runRerun} />
        )}
      </div>
    </div>
  );
}

function SummaryChip({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color,
      background: color + '14', border: `1px solid ${color}40`, borderRadius: 8, padding: '5px 11px',
    }}>
      {label} <strong style={{ fontSize: 14 }}>{value}</strong>
    </span>
  );
}

function ToggleChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer',
      padding: '7px 12px', borderRadius: 8,
      border: `1px solid ${active ? T.cyan : T.border}`,
      background: active ? 'rgba(18,181,196,0.10)' : '#fff',
      color: active ? T.cyanDim : T.fgSecondary,
    }}>{label}</button>
  );
}

// ── Pipeline strip ───────────────────────────────────────────
// Above the per-client table: is the machine that refreshes clients still
// running at all, independent of whether any single client's data is stale.

function PipelineFreshBar({ fresh }: { fresh: { count: number; total: number } | null }) {
  if (!fresh) return <span style={{ fontSize: 12, color: T.fgMuted }}>—</span>;
  const pct = fresh.total > 0 ? fresh.count / fresh.total : 0;
  const color = pct >= 0.9 ? T.green : pct >= 0.6 ? T.amber : T.magenta;
  return (
    <div style={{ minWidth: 88 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.fgPrimary, marginBottom: 4 }}>
        {fresh.count} / {fresh.total}
      </div>
      <div style={{ width: '100%', height: 7, borderRadius: 4, background: T.bgOverlay, overflow: 'hidden' }}>
        <div style={{ width: `${Math.round(pct * 100)}%`, height: '100%', borderRadius: 4, background: color }} />
      </div>
    </div>
  );
}

function PipelineResultPill({ pipeline }: { pipeline: PipelineStatus }) {
  if (pipeline.lastOk === null) {
    return <span style={{ fontSize: 11, color: T.fgMuted, fontStyle: 'italic' }}>Never logged</span>;
  }
  const color = pipeline.lastOk ? T.green : T.magenta;
  const label = pipeline.lastOk ? 'OK' : 'Failed';
  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 700, color,
        background: color + '14', border: `1px solid ${color}40`, borderRadius: 6, padding: '2px 8px',
      }}>{label}</span>
      {/* When something has quietly stopped, this is the single most useful
          thing on the screen — never hide it behind a tooltip. */}
      {!pipeline.lastOk && pipeline.lastError && (
        <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 4, maxWidth: 220 }}>
          {pipeline.lastError.slice(0, 90)}{pipeline.lastError.length > 90 ? '…' : ''}
        </div>
      )}
    </div>
  );
}

function PipelineRow({ pipeline, now }: { pipeline: PipelineStatus; now: number }) {
  return (
    <tr>
      <td style={tdBase()}>
        <div style={{ fontWeight: 700, fontSize: 12.5, color: T.fgPrimary }}>{pipeline.label}</div>
        <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 2 }}>{pipeline.detail}</div>
      </td>
      <td style={tdBase()}><span style={{ fontSize: 12, color: T.fgSecondary }}>{pipeline.cadence}</span></td>
      <td style={tdBase()}>
        {pipeline.nextRunAt ? (
          <span style={{ fontSize: 12, color: T.fgPrimary }}>{fmtDateTime(pipeline.nextRunAt)}</span>
        ) : (
          <span style={{ fontSize: 12, color: T.fgMuted, fontStyle: 'italic' }}>Event-driven</span>
        )}
      </td>
      <td style={tdBase()}>
        <div style={{ fontSize: 12, color: pipeline.lastRunAt ? T.fgPrimary : T.fgMuted }}>{fmtDateTime(pipeline.lastRunAt)}</div>
        {pipeline.lastRunAt && <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 2 }}>{agoLabel(pipeline.lastRunAt, now)}</div>}
      </td>
      <td style={tdBase()}><PipelineResultPill pipeline={pipeline} /></td>
      <td style={tdBase()}>
        <span style={{ fontSize: 12, color: pipeline.lastSuccessAt ? T.fgPrimary : T.fgMuted }}>{fmtDateTime(pipeline.lastSuccessAt)}</span>
      </td>
      <td style={tdBase()}><PipelineFreshBar fresh={pipeline.fresh} /></td>
    </tr>
  );
}

function PipelineStrip({ pipelines, now }: { pipelines: PipelineStatus[]; now: number }) {
  return (
    // Same "scrolls inside this box" pattern as the client table below — the
    // page itself never scrolls sideways.
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 960, width: '100%' }}>
        <thead>
          <tr>
            <Th>Pipeline</Th>
            <Th>Cadence</Th>
            <Th>Next run</Th>
            <Th>Last run</Th>
            <Th>Last result</Th>
            <Th>Last success</Th>
            <Th>Fresh clients</Th>
          </tr>
        </thead>
        <tbody>
          {pipelines.map(p => <PipelineRow key={p.key} pipeline={p} now={now} />)}
        </tbody>
      </table>
    </div>
  );
}

// ── Table ─────────────────────────────────────────────────────

function thBase(sticky?: boolean): React.CSSProperties {
  return {
    padding: '10px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: T.fgMuted, textAlign: 'left',
    borderBottom: `1px solid ${T.border}`, background: T.bgSubtle,
    position: 'sticky', top: 0, zIndex: sticky ? 3 : 2,
    left: sticky ? 0 : undefined, whiteSpace: 'nowrap',
  };
}

function tdBase(sticky?: boolean, background?: string): React.CSSProperties {
  return {
    padding: '10px 14px', verticalAlign: 'top', fontSize: 12, color: T.fgPrimary,
    borderBottom: `1px solid ${T.border}`, background,
    position: sticky ? 'sticky' : undefined, left: sticky ? 0 : undefined, zIndex: sticky ? 1 : undefined,
  };
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <th style={{ ...thBase(), textAlign: align ?? 'left' }}>{children}</th>;
}

function SortableTh({ label, sortKey, sort, onSort, sticky }: {
  label: string; sortKey: SortKey; sort: SortState; onSort: (key: SortKey) => void; sticky?: boolean;
}) {
  const active = sort.key === sortKey;
  return (
    <th style={thBase(sticky)}>
      <button onClick={() => onSort(sortKey)} style={{
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
        font: 'inherit', color: active ? T.cyanDim : T.fgMuted, display: 'inline-flex', alignItems: 'center', gap: 3,
      }}>
        {label}{active && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

function StatusTable({ rows, sort, onSort, actions, onRerun }: {
  rows: ClientStatusRow[];
  sort: SortState;
  onSort: (key: SortKey) => void;
  actions: Record<string, RowAction>;
  onRerun: (row: ClientStatusRow, target: ActionTarget) => void;
}) {
  return (
    // The table is wider than any screen, so it scrolls inside this box —
    // the page itself never scrolls sideways.
    <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'auto', maxHeight: '72vh' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 1560, width: '100%' }}>
        <thead>
          <tr>
            <SortableTh label="Client" sortKey="name" sort={sort} onSort={onSort} sticky />
            <Th>Stage</Th>
            {FEATURE_COLUMNS.map(col => (
              <SortableTh key={col.key} label={col.label} sortKey={col.key} sort={sort} onSort={onSort} />
            ))}
            <Th>Attention</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <StatusRow
              key={row.clientId ?? row.intakeId}
              row={row}
              action={row.clientId ? actions[row.clientId] : undefined}
              onRerun={onRerun}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusRow({ row, action, onRerun }: {
  row: ClientStatusRow;
  action?: RowAction;
  onRerun: (row: ClientStatusRow, target: ActionTarget) => void;
}) {
  // Faint amber wash so a row needing attention reads as "different" before
  // you've even looked at its cells.
  const rowBg = row.attention.length > 0 ? 'rgba(245,160,32,0.05)' : '#fff';
  return (
    <tr>
      <td style={tdBase(true, rowBg)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: T.fgPrimary }}>{row.name}</span>
          {row.isBranch && <GBIBadge color={T.purple} style={{ fontSize: 9, padding: '1px 6px' }}>branch</GBIBadge>}
        </div>
        <div style={{ fontSize: 11, color: T.fgMuted, marginTop: 2 }}>{row.location}</div>
      </td>
      <td style={tdBase(false, rowBg)}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
          <GBIBadge color={STAGE_COLOR[row.stage]}>{row.stage}</GBIBadge>
          <GBIBadge color={CAMPAIGN_COLOR[row.campaign]}>{row.campaign}</GBIBadge>
        </div>
      </td>
      {FEATURE_COLUMNS.map(col => (
        <td key={col.key} style={tdBase(false, rowBg)}>
          <FeatureCell status={row.features[col.key]} featureKey={col.key} cycleDay={row.cycleDay} />
        </td>
      ))}
      <td style={tdBase(false, rowBg)}>
        <AttentionCell items={row.attention} />
      </td>
      <td style={{ ...tdBase(false, rowBg), textAlign: 'right' }}>
        {row.clientId ? (
          <ActionsCell action={action} onRerun={target => onRerun(row, target)} />
        ) : (
          <span style={{ fontSize: 11, color: T.fgMuted, fontStyle: 'italic' }}>not imported yet</span>
        )}
      </td>
    </tr>
  );
}

function StateDot({ state }: { state: FeatureStatus['state'] }) {
  if (state === 'missing') {
    return <span style={{ width: 6, height: 6, borderRadius: '50%', border: `1.5px solid ${T.fgMuted}`, display: 'inline-block', flexShrink: 0 }} />;
  }
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: STATE_COLOR[state], display: 'inline-block', flexShrink: 0 }} />;
}

function FeatureCell({ status, featureKey, cycleDay }: { status: FeatureStatus; featureKey: ClientStatusFeatureKey; cycleDay: number | null }) {
  const dateLabel = fmtDate(status.lastUpdatedAt);
  // The heatmap's dueAt is a real scheduled date (the client's cycle day), not
  // an estimate extrapolated from freshness windows — drop the "~" for it.
  const isScheduled = featureKey === 'heatmap' && cycleDay != null;
  const nextLabel = status.state === 'missing' ? 'never run' : status.dueAt ? `next ${isScheduled ? '' : '~'}${fmtDate(status.dueAt)}` : null;
  return (
    <div title={fmtDateTime(status.lastUpdatedAt)} style={{ minWidth: 96 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <StateDot state={status.state} />
        <span style={{ fontSize: 13, fontWeight: 700, color: status.lastUpdatedAt ? T.fgPrimary : T.fgMuted }}>{dateLabel}</span>
      </div>
      {nextLabel && <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 2, marginLeft: 11 }}>{nextLabel}</div>}
      {status.detail && <div style={{ fontSize: 10.5, color: T.fgMuted, marginTop: 1, marginLeft: 11 }}>{status.detail}</div>}
    </div>
  );
}

function AttentionCell({ items }: { items: string[] }) {
  if (items.length === 0) return <Icon name="check" size={14} color={T.green} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 240 }}>
      {items.map((item, i) => {
        const severe = /failed|stuck|overdue|never run/i.test(item);
        const color = severe ? T.magenta : T.amber;
        return (
          <span key={i} title={item} style={{
            fontSize: 10.5, fontWeight: 600, color,
            background: color + '14', border: `1px solid ${color}40`, borderRadius: 6,
            padding: '2px 7px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{item}</span>
        );
      })}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 10, height: 10, border: `2px solid ${T.border}`, borderTopColor: T.cyan,
      borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite', marginRight: 4,
    }} />
  );
}

function ActionsCell({ action, onRerun }: { action?: RowAction; onRerun: (target: ActionTarget) => void }) {
  const busy = action?.loading != null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
      <div style={{ display: 'flex', gap: 6 }}>
        <GBIBtn variant="secondary" size="sm" disabled={busy} onClick={() => onRerun('aiInsight')} style={{ fontSize: 11, padding: '5px 10px' }}>
          {action?.loading === 'aiInsight' && <Spinner />}Rerun AI Insight
        </GBIBtn>
        {/* Quieter on purpose: this one ALSO spends Local Falcon credits + DataForSEO
            quota, so it must never read as equally "safe" to click as the one above. */}
        <GBIBtn variant="ghost" size="sm" disabled={busy} onClick={() => onRerun('data')} style={{
          fontSize: 11, padding: '5px 10px', color: T.fgMuted, borderColor: T.border, background: 'transparent',
        }}>
          {action?.loading === 'data' && <Spinner />}Full data refresh
        </GBIBtn>
      </div>
      {action?.message && (
        <div style={{
          fontSize: 10.5, color: action.message.kind === 'success' ? T.green : T.magenta,
          maxWidth: 220, textAlign: 'right',
        }}>{action.message.text}</div>
      )}
    </div>
  );
}
